import React from 'react'
import { Box, Typography, Chip, Table, TableBody, TableRow, TableCell, Tooltip } from '@mui/material'

interface MetadataCellProps {
  value: any
  t: any
}

const MAX_ITEMS = 3
const MAX_STRING_LENGTH = 100

const isEmptyValue = (val: any) =>
  val === undefined ||
  val === null ||
  (typeof val === 'object' && !Array.isArray(val) && Object.keys(val).length === 0) ||
  (Array.isArray(val) && val.length === 0)

const shorten = (txt: string, max = MAX_STRING_LENGTH) => (txt.length > max ? `${txt.slice(0, max)}...` : txt)

const MetadataCell: React.FC<MetadataCellProps> = ({ value, t }) => {
  const [showMore, setShowMore] = React.useState(false)

  if (isEmptyValue(value)) {
    return (
      <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>
        ---
      </Typography>
    )
  }

  // String simple
  if (typeof value === 'string') {
    const isLong = value.length > MAX_STRING_LENGTH
    return (
      <Box sx={{ width: '100%' }}>
        <Box
          sx={{
            p: 1,
            textAlign: 'left',
            whiteSpace: 'pre-line',
            wordBreak: 'break-word',
            fontSize: 12,
          }}
        >
          {showMore ? value : shorten(value)}
        </Box>
        {isLong && (
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 1 }}>
            <Chip
              label={showMore ? t('common:showLess') : t('common:showMore')}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setShowMore((prev) => !prev)
              }}
              size="small"
              sx={{
                backgroundColor: '#19DB55',
                color: '#fff',
                fontWeight: 500,
                height: 22,
                '&:hover': { backgroundColor: '#19DB55' },
              }}
            />
          </Box>
        )}
      </Box>
    )
  }

  // Normalizar arrays/objetos
  let entries: [string, any][]
  if (Array.isArray(value)) entries = value.map((v, i) => [`[${i}]`, v])
  else entries = Object.entries(value)

  const isLong = entries.length > MAX_ITEMS
  const visibleEntries = showMore ? entries : entries.slice(0, MAX_ITEMS)

  const renderVal = (val: any) => {
    if (val === null) return 'null'
    if (typeof val === 'object') {
      const json = JSON.stringify(val)
      return json.length > 60 ? (
        <Tooltip title={<pre style={{ margin: 0 }}>{JSON.stringify(val, null, 2)}</pre>} placement="left">
          <span>{shorten(json, 60)}</span>
        </Tooltip>
      ) : (
        json
      )
    }
    if (typeof val === 'string' && /^https?:\/\//i.test(val)) {
      return (
        <a
          href={val}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#1976d2', textDecoration: 'none' }}
          onClick={(e) => e.stopPropagation()}
        >
          {shorten(val, 60)}
        </a>
      )
    }
    return String(val)
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Table
        size="small"
        sx={{
          width: '100%',
          background: 'transparent',
          '& td': { border: 0 },
          '& tr:last-of-type td': { pb: 0 },
        }}
      >
        <TableBody>
          {visibleEntries.map(([k, v]) => (
            <TableRow key={k} hover={false}>
              <TableCell
                sx={{
                  p: 0.5,
                  pr: 1,
                  fontSize: 12,
                  fontWeight: 600,
                  verticalAlign: 'top',
                  width: '40%',
                  color: '#444',
                  wordBreak: 'break-word',
                }}
              >
                {k}
              </TableCell>
              <TableCell sx={{ p: 0.5, fontSize: 12, wordBreak: 'break-all', color: '#222' }}>{renderVal(v)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {isLong && (
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 1 }}>
          <Chip
            label={showMore ? t('common:showLess') : t('common:showMore')}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setShowMore((prev) => !prev)
            }}
            size="small"
            sx={{
              backgroundColor: '#19DB55',
              color: '#fff',
              fontWeight: 500,
              height: 22,
              '&:hover': { backgroundColor: '#19DB55' },
            }}
          />
        </Box>
      )}
    </Box>
  )
}

export default MetadataCell
